import { Injectable, ElementRef, NgZone, OnDestroy } from "@angular/core";
import { Observable, Subject, BehaviorSubject } from "rxjs";
import { debounceTime, takeUntil, distinctUntilChanged } from "rxjs/operators";

export interface ResizeState {
  width: number;
  height: number;
  isResizing: boolean;
}

@Injectable({
  providedIn: "root",
})
export class ResizeObserverService implements OnDestroy {
  private destroy$ = new Subject<void>();

  private observers = new Map<HTMLElement, ResizeObserver>();

  private resizeStates = new Map<HTMLElement, BehaviorSubject<ResizeState>>();

  private resizeTimeouts = new Map<HTMLElement, number>();

  constructor(private ngZone: NgZone) {}

  observeElement(
    element: ElementRef<HTMLElement> | HTMLElement,
    debounceMs: number = 0,
    resizingDurationMs: number = 250,
  ): Observable<ResizeState> {
    const el = element instanceof ElementRef ? element.nativeElement : element;

    if (this.resizeStates.has(el)) {
      return this.resizeStates.get(el)!.asObservable();
    }

    const resizeState$ = new BehaviorSubject<ResizeState>({
      width: el.offsetWidth,
      height: el.offsetHeight,
      isResizing: false,
    });

    this.resizeStates.set(el, resizeState$);

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length === 0) return;

      const entry = entries[0];
      if (!entry) return;

      const { width, height } = entry.contentRect;

      this.ngZone.run(() => {
        const existingTimeout = this.resizeTimeouts.get(el);
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        resizeState$.next({
          width,
          height,
          isResizing: true,
        });

        if (resizingDurationMs > 0) {
          const timeout = window.setTimeout(() => {
            resizeState$.next({
              width,
              height,
              isResizing: false,
            });
            this.resizeTimeouts.delete(el);
          }, resizingDurationMs);

          this.resizeTimeouts.set(el, timeout);
        } else {
          resizeState$.next({
            width,
            height,
            isResizing: false,
          });
        }
      });
    });

    resizeObserver.observe(el);
    this.observers.set(el, resizeObserver);

    const observable = resizeState$.asObservable().pipe(
      debounceMs > 0 ? debounceTime(debounceMs) : (source) => source,
      distinctUntilChanged((a, b) => a.width === b.width && a.height === b.height && a.isResizing === b.isResizing),
      takeUntil(this.destroy$),
    );

    return observable;
  }

  unobserve(element: ElementRef<HTMLElement> | HTMLElement): void {
    const el = element instanceof ElementRef ? element.nativeElement : element;

    const timeout = this.resizeTimeouts.get(el);
    if (timeout) {
      clearTimeout(timeout);
      this.resizeTimeouts.delete(el);
    }

    const observer = this.observers.get(el);
    if (observer) {
      observer.disconnect();
      this.observers.delete(el);
    }

    const subject = this.resizeStates.get(el);
    if (subject) {
      subject.complete();
      this.resizeStates.delete(el);
    }
  }

  getCurrentSize(element: ElementRef<HTMLElement> | HTMLElement): {
    width: number;
    height: number;
  } {
    const el = element instanceof ElementRef ? element.nativeElement : element;
    return {
      width: el.offsetWidth,
      height: el.offsetHeight,
    };
  }

  getCurrentState(element: ElementRef<HTMLElement> | HTMLElement): ResizeState | null {
    const el = element instanceof ElementRef ? element.nativeElement : element;
    const subject = this.resizeStates.get(el);
    return subject ? subject.value : null;
  }

  ngOnDestroy(): void {
    this.resizeTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.resizeTimeouts.clear();

    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();

    this.resizeStates.forEach((subject) => subject.complete());
    this.resizeStates.clear();

    this.destroy$.next();
    this.destroy$.complete();
  }
}
